/**
 * Public /roast landing — wraps the RoastWidget with SEO-optimized
 * H1, title, meta description, and breadcrumb. Used as the canonical
 * destination for the "Free Website Roast" CTA from the homepage and
 * any external links.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import RoastWidget from './WebsiteRoast';
import { useBreadcrumbSchema } from '../common/useBreadcrumbSchema';

const RoastPage = () => {
  // SEO: BreadcrumbList JSON-LD
  useBreadcrumbSchema('roast', useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'Free Website Roast', path: '/roast' },
  ], []));

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="roast-page">
      <>
        <title>Free Website Roast — AI Site Analysis | iSupplyAI</title>
        <meta name="description" content="Get a brutally honest AI critique of your website. 7 AI agents roast your positioning, copy, design, and conversion in 60 seconds. Free, no signup." />
        <link rel="canonical" href="https://isupplyai.com/roast" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="Free Website Roast — AI Site Analysis | iSupplyAI" />
        <meta property="og:description" content="7 AI agents roast your website in 60 seconds. Brutally honest. Free. No signup." />
        <meta property="og:url" content="https://isupplyai.com/roast" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:title" content="Free Website Roast — AI Site Analysis | iSupplyAI" />
        <meta name="twitter:description" content="7 AI agents roast your website in 60 seconds. Brutally honest. Free. No signup." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
      </>

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="roast-page-home-link">
            <img src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" alt="iSupplyAI" className="h-8 w-8 object-contain" />
            <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">iSupplyAI</span>
          </Link>
          <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors">Back to App</Link>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <nav className="flex items-center gap-2 text-sm text-gray-500" data-testid="roast-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-300">Free Website Roast</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-widest text-purple-400 uppercase mb-4">Free Tool · No Signup</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="roast-page-title">
            Free Website Roast
          </h1>
          <p className="text-lg text-gray-400 max-w-xl mx-auto mb-10">
            7 AI critics tear into your site's positioning, copy, conversion, and design — brutally honest, instantly actionable. Paste your URL and get roasted.
          </p>
        </div>
      </section>

      {/* Roast widget */}
      <section className="px-4 pb-20" data-testid="roast-widget-container">
        <div className="max-w-3xl mx-auto">
          <RoastWidget />
        </div>
      </section>

      {/* Related Tools (Sitewide internal linking) */}
      <section className="px-4 pb-16 border-t border-white/10 pt-12">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-white mb-6">Related free tools</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/strategy-score" className="block p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-purple-500/40 transition-colors" data-testid="roast-related-strategy-score">
              <p className="text-xs text-purple-400 mb-2 uppercase tracking-wider">Free Tool</p>
              <p className="text-base font-semibold text-white mb-1">Strategy Score</p>
              <p className="text-sm text-gray-400">Get scored across 6 strategy dimensions in 90 seconds.</p>
            </Link>
            <Link to="/compare" className="block p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-purple-500/40 transition-colors" data-testid="roast-related-compare">
              <p className="text-xs text-purple-400 mb-2 uppercase tracking-wider">Comparisons</p>
              <p className="text-base font-semibold text-white mb-1">iSupplyAI vs Alternatives</p>
              <p className="text-sm text-gray-400">See how we compare to Jasper, Copy.ai, HubSpot AI, and more.</p>
            </Link>
            <Link to="/blog" className="block p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-purple-500/40 transition-colors" data-testid="roast-related-blog">
              <p className="text-xs text-purple-400 mb-2 uppercase tracking-wider">Read</p>
              <p className="text-base font-semibold text-white mb-1">AI Marketing Blog</p>
              <p className="text-sm text-gray-400">Expert insights on AI marketing strategy & competitive intelligence.</p>
            </Link>
            <Link to="/glossary" className="block p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-purple-500/40 transition-colors" data-testid="roast-related-glossary">
              <p className="text-xs text-purple-400 mb-2 uppercase tracking-wider">Learn</p>
              <p className="text-base font-semibold text-white mb-1">AI Marketing Glossary</p>
              <p className="text-sm text-gray-400">Key terms and definitions in AI-powered marketing strategy.</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoastPage;
